// ─────────────────────────────────────────────────────────────────────────
// SchedulingQueueDrawer — right-side sheet that shows the orders of a
// specific queue (National Calls / Safeguard Internal / Today / Tomorrow /
// +2 / External / External Morning / Priority / Admin Review).
//
// Angela 2026-05-16. Reads the SAME orders array the table renders —
// business logic (predicates / counts) is NOT touched. The parent passes
// the already-filtered list so the drawer is purely presentational.
// ─────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useEffect } from "react";
import {
  X, ChevronLeft, Search, MapPin, Building2, Calendar, Tag, Phone,
} from "lucide-react";

const formatDateShort = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  // Accept both YYYY-MM-DD and ISO timestamps.
  if (s.length >= 10) return s.slice(0, 10);
  return s;
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const lower = String(status).toLowerCase();
  let tone = "bg-gray-100 text-gray-600";
  if (lower.includes("complete")) tone = "bg-[#A7C4A0]/15 text-[#4A7A56]";
  else if (lower.includes("hold")) tone = "bg-[#C07070]/15 text-[#8B4545]";
  else if (lower.includes("appointment")) tone = "bg-[#2F5D8A]/10 text-[#2F5D8A]";
  else if (lower.includes("scheduled")) tone = "bg-[#2F5D8A]/10 text-[#2F5D8A]";
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${tone}`}>
      {status}
    </span>
  );
};

const SchedulingQueueDrawer = ({
  open,
  onClose,
  queueTitle,
  queueOrders,
  onPickOrder,
}) => {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) setSearch("");
  }, [open, queueTitle]);

  const filtered = useMemo(() => {
    if (!search) return queueOrders || [];
    const s = search.toLowerCase();
    return (queueOrders || []).filter((o) => {
      const blob = [
        o.order_number, o.cliente, o.address, o.address1, o.city,
        o.state, o.zip, o.zip_code, o.order_type, o.status, o.due_date,
      ].filter(Boolean).join(" ").toLowerCase();
      return blob.includes(s);
    });
  }, [queueOrders, search]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
        data-testid="queue-drawer-backdrop"
      />
      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-label={queueTitle || "Queue"}
        className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-white shadow-2xl z-50 flex flex-col"
        data-testid="queue-drawer"
      >
        {/* Header */}
        <header className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-600"
            data-testid="queue-drawer-back"
            aria-label="Back"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-800 truncate" data-testid="queue-drawer-title">
              {queueTitle || "Queue"}
            </h2>
            <p className="text-[11px] text-gray-500">
              <strong data-testid="queue-drawer-count">{(queueOrders || []).length}</strong> order
              {(queueOrders || []).length === 1 ? "" : "s"}
              {search && filtered.length !== (queueOrders || []).length && (
                <> · <strong>{filtered.length}</strong> visible</>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400"
            data-testid="queue-drawer-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        {/* Search */}
        <div className="px-4 py-2 border-b border-gray-100">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search client, order, address…"
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:border-[#2F5D8A]"
              data-testid="queue-drawer-search"
            />
          </div>
        </div>
        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {filtered.length === 0 ? (
            <p
              className="text-xs text-gray-400 italic px-2 py-6 text-center"
              data-testid="queue-drawer-empty"
            >
              {search
                ? "No orders match your search."
                : "No orders in this queue right now."}
            </p>
          ) : (
            <ul className="space-y-1.5" data-testid="queue-drawer-list">
              {filtered.map((o) => (
                <li key={o.id || o._id || o.order_number}>
                  <button
                    onClick={() => onPickOrder && onPickOrder(o)}
                    className="w-full text-left bg-white border border-gray-200 hover:border-[#2F5D8A]/40 hover:shadow-sm rounded-lg px-3 py-2 transition-all"
                    data-testid={`queue-drawer-row-${o.order_number}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-0.5">
                      <span className="text-sm font-bold text-gray-800">
                        #{o.order_number}
                      </span>
                      <StatusBadge status={o.status} />
                    </div>
                    <p className="text-[11px] text-gray-600 truncate flex items-center gap-1">
                      <Building2 className="h-3 w-3 text-gray-400 shrink-0" />
                      {o.cliente || "—"}
                      {o.order_type && (
                        <span className="ml-auto inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-gray-100 text-[9px] uppercase tracking-wider">
                          <Tag className="h-2.5 w-2.5" />
                          {o.order_type}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-gray-500 truncate flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3 text-gray-400 shrink-0" />
                      {[o.address || o.address1, o.city, o.state]
                        .filter(Boolean)
                        .join(", ")}
                      {o.zip ? ` ${o.zip}` : o.zip_code ? ` ${o.zip_code}` : ""}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
                      {o.due_date && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Due {formatDateShort(o.due_date)}
                        </span>
                      )}
                      {o.phone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {o.phone}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
};

export default SchedulingQueueDrawer;
